import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ErrorState, LoadingState, PageHeader, StatusBadge, EmptyState } from "@/components/common/States";
import { ConfirmReasonDialog } from "@/components/common/ConfirmReasonDialog";
import { AuditTable } from "@/components/common/AuditTable";
import { api, fetcher } from "@/lib/api";
import { MEMBER_STATUS, ROLE_LABELS, TENANT_STATUS, formatDate } from "@/lib/labels";
import { SchoolAdminInvites } from "./SchoolAdminInvites";

const Info = ({ label, value }) => (
  <div><p className="text-xs text-muted-foreground">{label}</p><p className="font-medium break-words">{value || "—"}</p></div>
);

const StatusControl = ({ school, onChanged }) => {
  const [next, setNext] = useState("");
  const [open, setOpen] = useState(false);
  const options = Object.keys(TENANT_STATUS).filter((s) => s !== school.status);
  const confirm = async (reason) => {
    await api.patch(`/platform/schools/${school.id}/status`, { status: next, reason });
    toast.success("Status sekolah diperbarui.");
    setNext("");
    onChanged();
  };
  return (
    <div className="rounded-2xl border bg-card p-6 space-y-4">
      <h2 className="text-base md:text-lg font-bold">Status tenant</h2>
      <StatusBadge map={TENANT_STATUS} value={school.status} testId="school-detail-status" />
      <p className="text-xs text-muted-foreground">Alasan terakhir: {school.status_reason || "—"}</p>
      <div className="space-y-2">
        <Label>Ubah status menjadi</Label>
        <Select value={next} onValueChange={setNext}>
          <SelectTrigger aria-label="Status baru" data-testid="school-status-select"><SelectValue placeholder="Pilih status baru" /></SelectTrigger>
          <SelectContent>{options.map((s) => <SelectItem key={s} value={s}>{TENANT_STATUS[s].label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Button disabled={!next} onClick={() => setOpen(true)} className="w-full" data-testid="school-status-change-button">Ubah status</Button>
      <p className="text-xs text-muted-foreground">Status tenant tidak mengubah status langganan akun pengguna.</p>
      <ConfirmReasonDialog open={open} onOpenChange={setOpen} danger={next === "Deactivated" || next === "Restricted"} testId="status-change-dialog"
        title="Konfirmasi perubahan status" description={next ? `${TENANT_STATUS[school.status].label} → ${TENANT_STATUS[next].label} untuk ${school.name}.` : ""}
        confirmLabel="Ya, ubah status" onConfirm={confirm} />
    </div>
  );
};

const MetadataControl = ({ school, onChanged }) => {
  const [f, setF] = useState({ name: school.name, npsn: school.npsn || "" });
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const diff = [["name", "Nama sekolah"], ["npsn", "NPSN"]].filter(([k]) => (f[k] || "").trim() && (f[k] || "").trim() !== (school[k] || ""));
  const ask = () => {
    if (f.name.trim().length < 3) return setErr("Nama sekolah minimal 3 karakter.");
    if (f.npsn && !/^\d{8}$/.test(f.npsn)) return setErr("NPSN harus 8 digit angka.");
    if (!diff.length) return setErr("Belum ada perubahan.");
    setErr("");
    setOpen(true);
  };
  const confirm = async (reason) => {
    await api.patch(`/platform/schools/${school.id}/metadata`, { ...Object.fromEntries(diff.map(([k]) => [k, f[k].trim()])), reason });
    toast.success("Data sekolah dikoreksi.");
    onChanged();
  };
  return (
    <section className="rounded-2xl border bg-card p-6 space-y-4" data-testid="school-metadata-card">
      <h2 className="text-base md:text-lg font-bold">Koreksi data sekolah</h2>
      <div className="grid sm:grid-cols-[1fr_200px_auto] gap-3 items-end">
        <div className="space-y-1.5"><Label htmlFor="md-name">Nama sekolah</Label><Input id="md-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="metadata-name-input" /></div>
        <div className="space-y-1.5"><Label htmlFor="md-npsn">NPSN</Label><Input id="md-npsn" inputMode="numeric" value={f.npsn} onChange={(e) => setF({ ...f, npsn: e.target.value })} data-testid="metadata-npsn-input" /></div>
        <Button variant="outline" onClick={ask} data-testid="metadata-review-button">Tinjau perubahan</Button>
      </div>
      {err && <p className="text-sm text-[#E11D2E]" role="alert" data-testid="metadata-error">{err}</p>}
      <p className="text-xs text-muted-foreground">ID sekolah, status, anggota, dan riwayat tidak berubah.</p>
      <ConfirmReasonDialog open={open} onOpenChange={setOpen} title="Konfirmasi koreksi data" confirmLabel="Simpan koreksi" onConfirm={confirm} testId="metadata-confirm-dialog">
        <ul className="rounded-lg bg-muted p-3 text-sm space-y-1" data-testid="metadata-diff">
          {diff.map(([k, label]) => <li key={k}>{label}: <span className="line-through opacity-70">{school[k] || "—"}</span> → <b>{f[k].trim()}</b></li>)}
        </ul>
      </ConfirmReasonDialog>
    </section>
  );
};

const AdminRow = ({ a, schoolId, onChanged }) => {
  const [open, setOpen] = useState(false);
  const next = a.status === "active" ? "inactive" : "active";
  const confirm = async (reason) => {
    await api.patch(`/platform/schools/${schoolId}/admins/${a.membership_id}/status`, { status: next, reason });
    toast.success("Status Admin Sekolah diperbarui.");
    onChanged();
  };
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 p-4">
      <div><p className="font-medium">{a.full_name}</p><p className="text-xs text-muted-foreground">{a.email}</p></div>
      <div className="flex items-center gap-2">
        <StatusBadge map={MEMBER_STATUS} value={a.status} testId={`school-admin-status-${a.membership_id}`} />
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`school-admin-toggle-${a.membership_id}`}>
          {next === "inactive" ? "Nonaktifkan" : "Aktifkan"}
        </Button>
      </div>
      <ConfirmReasonDialog open={open} onOpenChange={setOpen} danger={next === "inactive"} testId="school-admin-status-dialog"
        title={next === "inactive" ? "Nonaktifkan Admin Sekolah?" : "Aktifkan kembali Admin Sekolah?"}
        description={next === "inactive" ? `${a.full_name} hanya dapat melihat data (mode baca saja).` : `${a.full_name} dapat kembali mengelola sekolah.`}
        confirmLabel={next === "inactive" ? "Nonaktifkan" : "Aktifkan"} onConfirm={confirm} />
    </li>
  );
};

export default function SchoolDetail() {
  const { id } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["platform-school", id], queryFn: () => fetcher(`/platform/schools/${id}`) });
  if (q.isLoading) return <LoadingState />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const { school, member_counts_by_role: counts, school_admins: admins, status_history: history } = q.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["platform-school", id] });
  return (
    <div className="space-y-8">
      <Link to="/pusat-kendali/sekolah" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground" data-testid="school-detail-back-link"><ArrowLeft className="h-4 w-4 mr-1" /> Kembali ke daftar</Link>
      <PageHeader eyebrow={school.id} title={school.name} description={school.is_demo ? "Sekolah ini adalah data demo." : "Metadata tenant. Data akademik tidak ditampilkan di Pusat Kendali."} />
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="rounded-2xl border bg-card p-6 grid sm:grid-cols-2 gap-5" data-testid="school-detail-info">
          <Info label="ID sekolah (tetap)" value={school.id} />
          <Info label="NPSN" value={school.npsn} />
          <Info label="Jenjang" value={school.level} />
          <Info label="Kabupaten/Kota, Provinsi" value={`${school.city}, ${school.province}`} />
          <Info label="Alamat" value={school.address} />
          <Info label="Kontak" value={[school.email, school.phone].filter(Boolean).join(" · ")} />
          <Info label="Dibuat" value={formatDate(school.created_at)} />
          <Info label="Jumlah keanggotaan" value={Object.entries(counts).map(([r, n]) => `${ROLE_LABELS[r]}: ${n}`).join(", ")} />
        </div>
        <StatusControl school={school} onChanged={refresh} />
      </div>
      <MetadataControl key={`${school.name}-${school.npsn}`} school={school} onChanged={refresh} />
      <section className="space-y-3">
        <h2 className="text-base md:text-lg font-bold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#0B3D91] dark:text-[#3B82F6]" /> Admin Sekolah</h2>
        {admins.length ? (
          <ul className="rounded-xl border divide-y" data-testid="school-admins-list">
            {admins.map((a) => <AdminRow key={a.membership_id} a={a} schoolId={school.id} onChanged={refresh} />)}
          </ul>
        ) : <EmptyState title="Belum ada Admin Sekolah" description="Buat tautan undangan di bawah ini." />}
      </section>
      <SchoolAdminInvites school={school} onChanged={refresh} />
      <section className="space-y-3">
        <h2 className="text-base md:text-lg font-bold">Riwayat tenant</h2>
        <AuditTable items={history} />
      </section>
    </div>
  );
}
